// src/components/Payment.jsx
import { lazy, Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import Spinner from './Spinner';

/* ======================================================================
 * PAY-2 - THE PROVIDER IS THE SERVER'S DECISION.
 * ----------------------------------------------------------------------
 * This component only picks which adapter to show, from the `provider` the
 * server put on the order ("paypal" | "bank"). It holds no provider logic of
 * its own and computes no money. Both adapters are separate lazy chunks, so a
 * patient on one provider never downloads the other's code.
 *
 * If the server later says a different provider at create time (the operator
 * flipped it mid-session), the adapter calls onProviderChange and this
 * component remounts the right one.
 *
 * An appointment copay is the same: the provider comes from the reserve
 * response, carried on payPalOrder.appointment.provider.
 * ====================================================================== */
const PaypalAdapter = lazy(() => import('./payment/PaypalAdapter'));
const BankAdapter = lazy(() => import('./payment/BankAdapter'));

function Payment(props) {
    const { payPalOrder } = props;
    const [override, setOverride] = useState(null);
    const provider = override || payPalOrder?.order?.provider || payPalOrder?.appointment?.provider;

    const adapterProps = { ...props, onProviderChange: setOverride };

    let adapter = null;
    if (provider === 'paypal') {
        adapter = <PaypalAdapter key="paypal" {...adapterProps} />;
    } else if (provider === 'bank') {
        adapter = <BankAdapter key="bank" {...adapterProps} />;
    }

    if (!adapter) {
        return (
            <div className="text-center p-4 text-red-600 bg-red-50 border border-red-200 rounded">
                We cannot take a payment right now. You have not been charged. Please try again shortly, or call the office.
            </div>
        );
    }

    return (
        <Suspense fallback={<Spinner />}>
            {adapter}
        </Suspense>
    );
}

Payment.propTypes = {
    value: PropTypes.string.isRequired,
    orderid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    payPalOrder: PropTypes.object,
};

export default Payment;
